import { useCallback, useEffect, useState } from 'react';
import { CompanyDetail } from '../../interfaces/companyDetail.interface';
import { getCompanyDetail } from '../../services/companyDetail';

export interface UseCompanyDetailResult {
  detail: CompanyDetail | null;
  loading: boolean;
  error: boolean;
  refetch: () => void;
}

/**
 * Fetches the lightweight company profile header from the company-detail
 * endpoint. Re-fetches when the company id or `reloadToken` changes, or when
 * `refetch` is invoked (e.g. after a header-affecting edit).
 */
export function useCompanyDetail(
  companyId: number | undefined,
  reloadToken: number = 0,
): UseCompanyDetailResult {
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<boolean>(false);
  const [internalToken, setInternalToken] = useState<number>(0);

  const refetch = useCallback(() => setInternalToken((token) => token + 1), []);

  useEffect(() => {
    if (companyId === undefined) {
      setDetail(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(false);

    getCompanyDetail(companyId)
      .then((response) => {
        if (!cancelled) setDetail(response);
      })
      .catch(() => {
        if (cancelled) return;
        setError(true);
        setDetail(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [companyId, reloadToken, internalToken]);

  return { detail, loading, error, refetch };
}
